import { FC } from 'react';
import { Stack } from '@mui/material';
import { Modal, PhoneInput, TextField } from 'melp-design/components';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useTranslation } from 'react-i18next';
import { ModalProps } from 'store/modal';
import {
  useCreateCompanyContact,
  useUpdateCompanyContact,
} from 'store/companies';
import { usePredefinedToasts } from 'utils/Toast';

interface Values {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

const INITIAL_VALUES: Values = {
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
};

interface Props extends ModalProps {
  companyId: string;
  contactId?: string;
  initialValues?: Values;
}

export const CompanyContactModal: FC<Props> = ({
  closeModal,
  companyId,
  contactId,
  initialValues,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();

  const { control, handleSubmit } = useForm<Values>({
    defaultValues: initialValues ?? INITIAL_VALUES,
  });

  const { mutate: createContact, isLoading: isCreating } =
    useCreateCompanyContact();
  const { mutate: updateContact, isLoading: isUpdating } =
    useUpdateCompanyContact();

  const onSubmit: SubmitHandler<Values> = (values) => {
    if (contactId) {
      updateContact(
        {
          contactId,
          data: {
            email: values.email || null,
            firstName: values.firstName || null,
            lastName: values.lastName || null,
            phone: values.phone || null,
          },
        },
        {
          onSuccess: () => {
            predefinedToasts.success.updated();
            closeModal();
          },
        },
      );
    } else {
      createContact(
        {
          companyId,
          data: {
            email: values.email || null,
            firstName: values.firstName || null,
            lastName: values.lastName || null,
            phone: values.phone || null,
          },
        },
        {
          onSuccess: () => {
            predefinedToasts.success.added();
            closeModal();
          },
        },
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('marketplaceOrders.contactPerson')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.save'),
            type: 'submit',
            disabled: isCreating || isUpdating,
          },
        ]}
      >
        <Stack spacing={2}>
          <Controller
            name="firstName"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('form.first_name')}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="lastName"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('form.last_name')}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="phone"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <PhoneInput
                label={t('form.mobile_number')}
                name={name}
                value={value}
                onChange={onChange}
                ref={ref}
              />
            )}
          />

          <Controller
            name="email"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <TextField
                label={t('common.email')}
                name={name}
                value={value}
                onChange={onChange}
                type="email"
                ref={ref}
              />
            )}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
